import { useLocalization } from "../../../features/localization/index.js";
import DataTable_default from "../../../components/data-table/DataTable.vue.js";
import DataTableCell_default from "../../../components/data-table/DataTableCell.vue.js";
import DataTableRow_default from "../../../components/data-table/DataTableRow.vue.js";
import { getOauth2AcquisitionTarget } from "../helpers/security-scheme.js";
import { refreshOauth2Token } from "../helpers/oauth.js";
import { getVisibleOrderedFlowKeys } from "../helpers/oauth-flows.js";
import { runOAuth2Authorize, storeOAuth2Tokens } from "../helpers/run-oauth2-authorize.js";
import RequestAuthDataTableInput_default from "./RequestAuthDataTableInput.vue.js";
import OAuth2_default from "./OAuth2.vue.js";
import OAuth2Metadata_default from "./OAuth2Metadata.vue.js";
import OpenIDConnect_default from "./OpenIDConnect.vue.js";
import { Fragment, capitalize, computed, createBlock, createCommentVNode, createElementBlock, createElementVNode, createTextVNode, createVNode, defineComponent, normalizeClass, openBlock, ref, renderList, toDisplayString, unref, withCtx } from "vue";
import { ScalarButton } from "@scalar/components/button";
import { ScalarIconGear } from "@scalar/icons";
import { ScalarModal, useModal } from "@scalar/components/modal";
import { ScalarIconButton } from "@scalar/components/icon-button";
import { getEnvironmentVariables } from "@scalar/workspace-store/request-example";
import { useToasts } from "@scalar/use-toasts";
import { getResolvedRef } from "@scalar/workspace-store/helpers/get-resolved-ref";
import { ScalarMarkdownSummary } from "@scalar/components/markdown";
import { useLoadingState } from "@scalar/components/loading";
import { getDocumentTypeLabel } from "@scalar/workspace-store/schemas/type-guards";
//#region src/v2/blocks/scalar-auth-selector-block/components/RequestAuthTab.vue?vue&type=script&setup=true&lang.ts
var _hoisted_1 = { class: "bg-b-1 min-w-0 flex-1 px-3 py-1.25" };
var _hoisted_2 = { class: "text-c-1 leading-5.5 font-medium" };
var _hoisted_3 = { class: "flex h-8 items-center gap-2 border-t pl-3 text-base" };
var _hoisted_4 = { class: "text-c-1 mr-auto" };
var _hoisted_5 = { class: "flex min-h-8 border-t text-base" };
var _hoisted_6 = { class: "flex h-8 max-w-full gap-2.5 overflow-x-auto px-3" };
var _hoisted_7 = ["onClick"];
var _hoisted_8 = { class: "relative z-10" };
var _hoisted_9 = {
	key: 5,
	class: "text-c-3 flex items-center justify-center border-t p-4 px-4 text-center text-xs text-balance"
};
var _hoisted_10 = {
	key: 6,
	class: "text-c-3 flex items-center justify-center border-t p-4 px-4 text-center text-xs text-balance"
};
var _hoisted_11 = {
	key: 7,
	class: "text-c-3 flex items-center justify-center border-t p-4 px-4 text-center text-xs text-balance"
};
var _hoisted_12 = {
	key: 8,
	class: "text-c-3 flex items-center justify-center border-t p-4 px-4 text-center text-xs text-balance"
};
var _hoisted_13 = { class: "flex h-8 items-center justify-end gap-2 border-t" };
/**
* Human-readable name of the document type.
* Used in the missing-type warning so it points at the correct document
* (e.g. "AsyncAPI") instead of always naming OpenAPI.
*/
var RequestAuthTab_vue_vue_type_script_setup_true_lang_default = /*@__PURE__*/ defineComponent({
	__name: "RequestAuthTab",
	props: {
		environment: {},
		isStatic: { type: Boolean },
		proxyUrl: {},
		selectedSecuritySchemas: {},
		securitySchemes: {},
		server: {},
		eventBus: {},
		options: {},
		documentType: { default: "openapi" }
	},
	emits: [
		"update:selectedScopes",
		"upsert:scope",
		"delete:scope"
	],
	setup(__props, { emit: __emit }) {
		const emits = __emit;
		const { translate } = useLocalization();
		const documentTypeLabel = computed(() => getDocumentTypeLabel(__props.documentType));
		/**
		* Resolves security schemes from the OpenAPI document and combines them with their selected scopes.
		* Each item includes the scheme definition, name, and associated scopes.
		*/
		const security = computed(() => Object.entries(__props.selectedSecuritySchemas).map(([name, scopes = []]) => {
			const scheme = getResolvedRef(__props.securitySchemes[name]);
			return {
				scheme,
				name,
				scopes,
				visibleFlowKeys: scheme?.type === "oauth2" || scheme?.type === "openIdConnect" ? getVisibleOrderedFlowKeys(scheme.flows) : []
			};
		}));
		/** Tracks which OAuth2 flow is currently active when multiple flows are available. */
		const activeFlow = ref("");
		/**
		* Keeps the selected flow in sync with the currently visible flow keys. Validating against the
		* visible set (not every declared flow) means a selection that becomes hidden — e.g. the document
		* is updated to mark the active flow with `x-scalar-ignore` — resets, so the default (first visible)
		* flow activates instead of leaving no tab selected.
		*/
		const selectedFlow = computed(() => {
			return security.value.flatMap((item) => item.visibleFlowKeys).some((key) => key === activeFlow.value) ? activeFlow.value : "";
		});
		const setActiveFlow = (flow) => {
			activeFlow.value = flow;
		};
		/** Determines if multiple auth schemes are configured, which affects the UI layout. */
		const hasMultipleSchemes = computed(() => security.value.length > 1);
		/**
		* Generates a human-readable label for the security scheme.
		* Intentionally omits the description here — it is rendered separately as
		* rich text via ScalarMarkdownSummary so markdown formatting is preserved.
		*/
		const generateLabel = (name, scheme) => {
			const capitalizedName = capitalize(name);
			switch (scheme.type) {
				case "apiKey": return `${capitalizedName}: ${scheme.in}`;
				case "openIdConnect":
				case "oauth2": {
					const firstFlow = getVisibleOrderedFlowKeys(scheme.flows)[0];
					const currentFlow = selectedFlow.value || firstFlow;
					if (!currentFlow) return capitalizedName;
					return `${capitalizedName}: ${currentFlow}`;
				}
				case "http": return `${capitalizedName}: ${scheme.scheme}`;
				default: return capitalizedName;
			}
		};
		/**
		* Whether an API key scheme exposes an editable parameter name.
		*
		* OpenAPI `apiKey` and AsyncAPI `httpApiKey` (normalized to `apiKey`) name a query/header/cookie
		* parameter, so the Name input is shown. AsyncAPI `apiKey` places the key in the broker `user` or
		* `password` slot and has no parameter name, so the Name input is hidden and only the value is asked for.
		*/
		const apiKeyHasName = (scheme) => scheme.in !== "user" && scheme.in !== "password";
		/**
		* The scheme's type when it is one we do not render inputs for (a type outside both the OpenAPI
		* and AsyncAPI security scheme unions). Read through a helper so the fallback template branch,
		* where the scheme type is narrowed to `never` after the supported cases, can still surface the type.
		*/
		const getUnsupportedSchemeType = (scheme) => scheme?.type;
		/**
		* Determines if an OAuth2 flow tab should be active.
		* The first flow is active by default if no flow is explicitly selected.
		*/
		const isFlowActive = (flowKey, index) => selectedFlow.value === flowKey || index === 0 && !selectedFlow.value;
		/** Computes the container class for static display mode. */
		const getStaticBorderClass = () => __props.isStatic && "border-t";
		const handleHttpSecretsUpdate = (payload, name) => __props.eventBus.emit("auth:update:security-scheme-secrets", {
			payload: {
				type: "http",
				...payload
			},
			name
		});
		const handleApiKeySecretsUpdate = (payload, name) => __props.eventBus.emit("auth:update:security-scheme-secrets", {
			payload: {
				type: "apiKey",
				...payload
			},
			name
		});
		const handleApiKeySecuritySchemeUpdate = (payload, name) => __props.eventBus.emit("auth:update:security-scheme", {
			payload: {
				type: "apiKey",
				...payload
			},
			name
		});
		/** Handles scope selection updates for OAuth2 */
		const handleScopesUpdate = (name, event) => {
			emits("update:selectedScopes", {
				id: Object.keys(__props.selectedSecuritySchemas),
				name,
				...event
			});
		};
		/** Handles scope-definition upserts (add / rename / description change) */
		const handleScopeUpsert = (name, event) => {
			emits("upsert:scope", {
				...event,
				name
			});
		};
		/** Handles scope-definition deletes */
		const handleScopeDelete = (name, event) => {
			emits("delete:scope", {
				...event,
				name
			});
		};
		/**
		* Computes dynamic classes for OAuth2 flow tabs based on active state.
		*/
		const getFlowTabClasses = (flowKey, index) => {
			const baseClasses = "floating-bg text-c-3 relative cursor-pointer border-b border-transparent py-1 text-base font-medium";
			return isFlowActive(flowKey, index) ? `${baseClasses} !text-c-1 !rounded-none border-b !border-current ${__props.isStatic ? "opacity-100" : ""}` : baseClasses;
		};
		/**
		* OAuth2 token-acquisition target (an oauth2 scheme with an interactive grant) sourced from
		* the merged schemes. When the active scheme is HTTP bearer, this powers the inline
		* "Authorize via OAuth2" shortcut: the flow's bearer token is written onto the bearer scheme,
		* so the panel never switches to oauth2.
		*
		* The shortcut is purely a convenience, so it is offered for any interactive oauth2 scheme —
		* including one that is also independently selectable in the dropdown (e.g. Scalar Galaxy's
		* `oAuth2`). Whether that oauth2 scheme is *hidden* from the dropdown is a separate decision made
		* in the selector; here we only surface the shortcut.
		*/
		const oauth2Target = computed(() => getOauth2AcquisitionTarget(__props.securitySchemes));
		/** Resolved oauth2 scheme object, for embedding its config form behind the gear. */
		const oauth2Scheme = computed(() => oauth2Target.value ? getResolvedRef(__props.securitySchemes[oauth2Target.value.name]) : void 0);
		const acquisitionLoader = useLoadingState();
		const { toast } = useToasts();
		const configModal = useModal();
		/** Bearer scheme that opened the config modal, so its Authorize callback targets it. */
		const configBearerName = ref();
		/** Runs the OAuth2 flow and writes the resulting bearer token onto the bearer scheme. */
		const handleAcquisitionAuthorize = async (bearerName) => {
			const target = oauth2Target.value;
			if (!target || acquisitionLoader.isLoading) return;
			acquisitionLoader.start();
			const [error] = await runOAuth2Authorize({
				eventBus: __props.eventBus,
				bearerSchemeName: bearerName,
				oauth2Name: target.name,
				flows: target.flows,
				flowType: target.flowType,
				scopes: target.scopes,
				server: __props.server,
				proxyUrl: __props.proxyUrl,
				environment: __props.environment,
				options: __props.options
			});
			await acquisitionLoader.clear();
			if (error) toast(error?.message ?? translate("apiClient.requestAuthTab.failedToauthorize"), "error");
		};
		/** Refreshes the OAuth2 token and rewrites it onto the bearer scheme. */
		const handleAcquisitionRefresh = async (bearerName) => {
			const target = oauth2Target.value;
			if (!target || target.flowType !== "authorizationCode" || acquisitionLoader.isLoading) return;
			acquisitionLoader.start();
			const [error, tokens] = await refreshOauth2Token(target.flows, target.flowType, __props.proxyUrl, __props.server, getEnvironmentVariables(__props.environment), __props.options?.customFetch);
			await acquisitionLoader.clear();
			if (tokens?.accessToken) storeOAuth2Tokens(__props.eventBus, {
				bearerSchemeName: bearerName,
				oauth2Name: target.name,
				flowType: target.flowType,
				tokens
			});
			else if (error) toast(error?.message ?? translate("apiClient.requestAuthTab.failedToRefresh"), "error");
		};
		const openAcquisitionConfig = (bearerName) => {
			configBearerName.value = bearerName;
			configModal.show();
		};
		/** Closes the config modal and runs Authorize against the bearer scheme that opened it. */
		const handleConfigAuthorize = () => {
			const bearerName = configBearerName.value;
			configModal.hide();
			if (bearerName) handleAcquisitionAuthorize(bearerName);
		};
		return (_ctx, _cache) => {
			return openBlock(), createElementBlock(Fragment, null, [(openBlock(true), createElementBlock(Fragment, null, renderList(security.value, ({ scheme, name, scopes, visibleFlowKeys }) => {
				return openBlock(), createElementBlock(Fragment, { key: name }, [
					hasMultipleSchemes.value && scheme ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 0 }, {
						default: withCtx(() => [createVNode(unref(DataTableCell_default), {
							"aria-label": generateLabel(name, scheme),
							class: "max-h-[auto]"
						}, {
							default: withCtx(() => [createElementVNode("div", _hoisted_1, [createElementVNode("p", _hoisted_2, toDisplayString(generateLabel(name, scheme)), 1), scheme.description ? (openBlock(), createBlock(unref(ScalarMarkdownSummary), {
								key: 0,
								class: "auth-description text-c-2 w-full",
								value: scheme.description
							}, null, 8, ["value"])) : createCommentVNode("", true)])]),
							_: 2
						}, 1032, ["aria-label"])]),
						_: 2
					}, 1024)) : createCommentVNode("", true),
					scheme?.description && !hasMultipleSchemes.value ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 1 }, {
						default: withCtx(() => [createVNode(unref(DataTableCell_default), {
							"aria-label": scheme.description,
							class: "max-h-[auto]"
						}, {
							default: withCtx(() => [createVNode(unref(ScalarMarkdownSummary), {
								class: "auth-description bg-b-1 text-c-2 min-w-0 flex-1 px-3 py-1.25",
								value: scheme.description
							}, null, 8, ["value"])]),
							_: 2
						}, 1032, ["aria-label"])]),
						_: 2
					}, 1024)) : createCommentVNode("", true),
					scheme?.type === "http" ? (openBlock(), createElementBlock(Fragment, { key: 2 }, [scheme.scheme === "bearer" ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 0 }, {
						default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
							canReset: "",
							containerClass: getStaticBorderClass(),
							environment: __props.environment,
							modelValue: scheme["x-scalar-secret-token"],
							placeholder: unref(translate)("apiClient.requestAuthTab.token"),
							type: "password",
							onReset: ($event) => __props.eventBus.emit("auth:reset:security-scheme-secret", {
								name,
								field: "x-scalar-secret-token"
							}),
							"onUpdate:modelValue": (v) => handleHttpSecretsUpdate({ "x-scalar-secret-token": v }, name)
						}, {
							default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.requestAuthTab.bearerToken")), 1)]),
							_: 1
						}, 8, [
							"containerClass",
							"environment",
							"modelValue",
							"placeholder",
							"onReset",
							"onUpdate:modelValue"
						])]),
						_: 2
					}, 1024)) : createCommentVNode("", true), scheme.scheme === "bearer" && oauth2Target.value ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 1 }, {
						default: withCtx(() => [createElementVNode("div", _hoisted_3, [
							createElementVNode("span", _hoisted_4, toDisplayString(unref(translate)("apiClient.requestAuthTab.getAToken")), 1),
							createVNode(unref(ScalarButton), {
								class: "text-c-1 px-3 py-1",
								loader: unref(acquisitionLoader),
								size: "sm",
								type: "button",
								variant: "gradient",
								onClick: ($event) => handleAcquisitionAuthorize(name)
							}, {
								default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.requestAuthTab.authorizeVia", { name: oauth2Target.value.name })), 1)]),
								_: 2
							}, 1032, ["loader", "onClick"]),
							scheme["x-scalar-secret-token"] && oauth2Target.value.flowType === "authorizationCode" ? (openBlock(), createBlock(unref(ScalarButton), {
								key: 0,
								class: "px-3 py-1",
								disabled: unref(acquisitionLoader).isLoading,
								size: "sm",
								type: "button",
								variant: "outlined",
								onClick: ($event) => handleAcquisitionRefresh(name)
							}, {
								default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.requestAuthTab.refresh")), 1)]),
								_: 1
							}, 8, ["disabled", "onClick"])) : createCommentVNode("", true),
							createVNode(unref(ScalarIconButton), {
								class: "mr-1.25 h-6 w-6 p-1.25",
								icon: unref(ScalarIconGear),
								label: unref(translate)("apiClient.requestAuthTab.configure", { name: oauth2Target.value.name }),
								onClick: ($event) => openAcquisitionConfig(name)
							}, null, 8, [
								"icon",
								"label",
								"onClick"
							])
						])]),
						_: 2
					}, 1024)) : scheme?.scheme === "basic" ? (openBlock(), createElementBlock(Fragment, { key: 2 }, [createVNode(unref(DataTableRow_default), null, {
						default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
							canReset: "",
							class: "text-c-2",
							environment: __props.environment,
							modelValue: scheme["x-scalar-secret-username"],
							placeholder: "janedoe",
							required: "",
							onReset: ($event) => __props.eventBus.emit("auth:reset:security-scheme-secret", {
								name,
								field: "x-scalar-secret-username"
							}),
							"onUpdate:modelValue": (v) => handleHttpSecretsUpdate({ "x-scalar-secret-username": v }, name)
						}, {
							default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.requestAuthTab.username")), 1)]),
							_: 1
						}, 8, [
							"environment",
							"modelValue",
							"onReset",
							"onUpdate:modelValue"
						])]),
						_: 2
					}, 1024), createVNode(unref(DataTableRow_default), null, {
						default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
							canReset: "",
							environment: __props.environment,
							modelValue: scheme["x-scalar-secret-password"],
							placeholder: "********",
							type: "password",
							onReset: ($event) => __props.eventBus.emit("auth:reset:security-scheme-secret", {
								name,
								field: "x-scalar-secret-password"
							}),
							"onUpdate:modelValue": (v) => handleHttpSecretsUpdate({ "x-scalar-secret-password": v }, name)
						}, {
							default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.requestAuthTab.password")), 1)]),
							_: 1
						}, 8, [
							"environment",
							"modelValue",
							"onReset",
							"onUpdate:modelValue"
						])]),
						_: 2
					}, 1024)], 64)) : createCommentVNode("", true)], 64)) : scheme?.type === "apiKey" ? (openBlock(), createElementBlock(Fragment, { key: 3 }, [apiKeyHasName(scheme) ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 0 }, {
						default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
							containerClass: getStaticBorderClass(),
							environment: __props.environment,
							modelValue: scheme.name ?? "",
							placeholder: "api-key",
							"onUpdate:modelValue": (v) => handleApiKeySecuritySchemeUpdate({ name: v }, name)
						}, {
							default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.requestAuthTab.name")), 1)]),
							_: 1
						}, 8, [
							"containerClass",
							"environment",
							"modelValue",
							"onUpdate:modelValue"
						])]),
						_: 2
					}, 1024)) : createCommentVNode("", true), createVNode(unref(DataTableRow_default), null, {
						default: withCtx(() => [createVNode(RequestAuthDataTableInput_default, {
							canReset: "",
							containerClass: apiKeyHasName(scheme) ? void 0 : getStaticBorderClass(),
							environment: __props.environment,
							modelValue: scheme["x-scalar-secret-token"],
							placeholder: "QUxMIFlPVVIgQkFTRSBBUkUgQkVMT05HIFRPIFVT",
							type: "password",
							onReset: ($event) => __props.eventBus.emit("auth:reset:security-scheme-secret", {
								name,
								field: "x-scalar-secret-token"
							}),
							"onUpdate:modelValue": (v) => handleApiKeySecretsUpdate({ "x-scalar-secret-token": v }, name)
						}, {
							default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.requestAuthTab.value")), 1)]),
							_: 1
						}, 8, [
							"containerClass",
							"environment",
							"modelValue",
							"onReset",
							"onUpdate:modelValue"
						])]),
						_: 2
					}, 1024)], 64)) : scheme?.type === "oauth2" || scheme?.type === "openIdConnect" ? (openBlock(), createElementBlock(Fragment, { key: 4 }, [
						scheme.type === "oauth2" && (!__props.isStatic || scheme.oauth2MetadataUrl) ? (openBlock(), createBlock(OAuth2Metadata_default, {
							key: 0,
							customFetch: __props.options?.customFetch,
							environment: __props.environment,
							eventBus: __props.eventBus,
							name,
							proxyUrl: __props.proxyUrl,
							scheme
						}, null, 8, [
							"customFetch",
							"environment",
							"eventBus",
							"name",
							"proxyUrl",
							"scheme"
						])) : createCommentVNode("", true),
						scheme?.type === "openIdConnect" && !visibleFlowKeys.length ? (openBlock(), createBlock(OpenIDConnect_default, {
							key: 1,
							customFetch: __props.options?.customFetch,
							environment: __props.environment,
							eventBus: __props.eventBus,
							getStaticBorderClass,
							name,
							proxyUrl: __props.proxyUrl,
							scheme
						}, null, 8, [
							"customFetch",
							"environment",
							"eventBus",
							"name",
							"proxyUrl",
							"scheme"
						])) : createCommentVNode("", true),
						visibleFlowKeys.length > 1 ? (openBlock(), createBlock(unref(DataTableRow_default), { key: 2 }, {
							default: withCtx(() => [createElementVNode("div", _hoisted_5, [createElementVNode("div", _hoisted_6, [(openBlock(true), createElementBlock(Fragment, null, renderList(visibleFlowKeys, (key, ind) => {
								return openBlock(), createElementBlock("button", {
									key,
									class: normalizeClass(getFlowTabClasses(key, ind)),
									type: "button",
									onClick: ($event) => setActiveFlow(key)
								}, [createElementVNode("span", _hoisted_8, toDisplayString(key), 1)], 10, _hoisted_7);
							}), 128))])])]),
							_: 2
						}, 1024)) : createCommentVNode("", true),
						(openBlock(true), createElementBlock(Fragment, null, renderList(visibleFlowKeys, (key, ind) => {
							return openBlock(), createElementBlock(Fragment, { key }, [scheme.flows && isFlowActive(key, ind) ? (openBlock(), createBlock(OAuth2_default, {
								key: 0,
								environment: __props.environment,
								eventBus: __props.eventBus,
								flows: scheme.flows,
								name,
								options: __props.options,
								proxyUrl: __props.proxyUrl,
								scheme,
								selectedScopes: scopes,
								server: __props.server,
								type: key,
								"onDelete:scope": (event) => handleScopeDelete(name, event),
								"onUpdate:selectedScopes": (event) => handleScopesUpdate(name, event),
								"onUpsert:scope": (event) => handleScopeUpsert(name, event)
							}, null, 8, [
								"environment",
								"eventBus",
								"flows",
								"name",
								"options",
								"proxyUrl",
								"scheme",
								"selectedScopes",
								"server",
								"type",
								"onDelete:scope",
								"onUpdate:selectedScopes",
								"onUpsert:scope"
							])) : createCommentVNode("", true)], 64);
						}), 128))
					], 64)) : scheme?.type === "userPassword" || scheme?.type === "plain" || scheme?.type === "scramSha256" || scheme?.type === "scramSha512" || scheme?.type === "X509" || scheme?.type === "symmetricEncryption" || scheme?.type === "asymmetricEncryption" || scheme?.type === "gssapi" ? (openBlock(), createElementBlock("div", _hoisted_9, toDisplayString(unref(translate)("apiClient.requestAuthTab.unsupportedCredentials")), 1)) : scheme?.type === "mutualTLS" ? (openBlock(), createElementBlock("div", _hoisted_10, toDisplayString(unref(translate)("apiClient.requestAuthTab.mutualTlsHint")), 1)) : getUnsupportedSchemeType(scheme) ? (openBlock(), createElementBlock("div", _hoisted_11, toDisplayString(unref(translate)("apiClient.requestAuthTab.unsupportedType", { type: getUnsupportedSchemeType(scheme) ?? "" })), 1)) : (openBlock(), createElementBlock("div", _hoisted_12, toDisplayString(unref(translate)("apiClient.requestAuthTab.missingType", { type: documentTypeLabel.value })), 1))
				], 64);
			}), 128)), oauth2Target.value && oauth2Scheme.value ? (openBlock(), createBlock(unref(ScalarModal), {
				key: 0,
				size: "sm",
				state: unref(configModal),
				title: unref(translate)("apiClient.requestAuthTab.configure", { name: oauth2Target.value.name })
			}, {
				default: withCtx(() => [createVNode(unref(DataTable_default), {
					columns: [""],
					presentational: ""
				}, {
					default: withCtx(() => [createVNode(OAuth2_default, {
						environment: __props.environment,
						eventBus: __props.eventBus,
						flows: oauth2Target.value.flows,
						hideActions: "",
						name: oauth2Target.value.name,
						options: __props.options,
						proxyUrl: __props.proxyUrl,
						scheme: oauth2Scheme.value,
						selectedScopes: oauth2Target.value.scopes,
						server: __props.server,
						type: oauth2Target.value.flowType,
						"onDelete:scope": _cache[0] || (_cache[0] = (event) => handleScopeDelete(oauth2Target.value.name, event)),
						"onUpdate:selectedScopes": _cache[1] || (_cache[1] = (event) => handleScopesUpdate(oauth2Target.value.name, event)),
						"onUpsert:scope": _cache[2] || (_cache[2] = (event) => handleScopeUpsert(oauth2Target.value.name, event))
					}, null, 8, [
						"environment",
						"eventBus",
						"flows",
						"name",
						"options",
						"proxyUrl",
						"scheme",
						"selectedScopes",
						"server",
						"type"
					])]),
					_: 1
				}), createElementVNode("div", _hoisted_13, [createVNode(unref(ScalarButton), {
					class: "p-0 px-2 py-0.5",
					size: "sm",
					variant: "outlined",
					onClick: _cache[3] || (_cache[3] = ($event) => unref(configModal).hide())
				}, {
					default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.requestAuthTab.cancel")), 1)]),
					_: 1
				}), createVNode(unref(ScalarButton), {
					class: "p-0 px-2 py-0.5",
					loader: unref(acquisitionLoader),
					size: "sm",
					onClick: handleConfigAuthorize
				}, {
					default: withCtx(() => [createTextVNode(toDisplayString(unref(translate)("apiClient.requestAuthTab.authorize")), 1)]),
					_: 1
				}, 8, ["loader"])])]),
				_: 1
			}, 8, ["state", "title"])) : createCommentVNode("", true)], 64);
		};
	}
});
//#endregion
export { RequestAuthTab_vue_vue_type_script_setup_true_lang_default as default };

//# sourceMappingURL=RequestAuthTab.vue.script.js.map